import type { DimensionDescriptor, MeasureDrillDownInfo } from "../../../../../../../../../api/types/svTypes.ts";
import { MeasureUnitTable } from "../../../../../../../../../api/types/svTypes.ts";
import {
  extensionLedgerDataModelNames,
  getExtensionLedger,
  getExtensionLedgerByDataModelName,
} from "./extensionLedgers";

/**
 * Backing data-model names of every transactional ledger that can host drill-down rows.
 * A dimension is considered "ledger-bound" when its DataModelNames intersect this set —
 * otherwise it's a joined dimension (e.g. Investor, Entity) and is available in every ledger.
 */
const ledgerDataModels: ReadonlySet<string> = new Set([
  "GeneralLedger",
  "MemoLedger",
  "AllocatedGeneralLedger",
  "AllocatedMemoLedger",
  ...extensionLedgerDataModelNames,
]);

export const getLedgerDataModelName = (info: MeasureDrillDownInfo): string | undefined => {
  const extensionLedger = getExtensionLedger(info.table);
  if (extensionLedger !== undefined) {
    return extensionLedger.dataModelName;
  }

  switch (info.table) {
    case MeasureUnitTable.Gl:
      return info.allocated ? "AllocatedGeneralLedger" : "GeneralLedger";
    case MeasureUnitTable.Memo:
      return info.allocated ? "AllocatedMemoLedger" : "MemoLedger";
    default:
      return undefined;
  }
};

export const isDimensionApplicableToLedger = (
  dimension: DimensionDescriptor,
  ledgerDataModel: string | undefined
): boolean => {
  if (!ledgerDataModel) {
    return true;
  }

  const dimLedgers = (dimension.dataModelNames ?? []).filter((name) => ledgerDataModels.has(name));
  // Universal / joined dimension — no ledger affinity, always available
  if (dimLedgers.length === 0) {
    return true;
  }

  if (dimLedgers.includes(ledgerDataModel)) {
    return true;
  }

  // An extension ledger's rows are rows of the ledger it extends, so that ledger's dimensions resolve too.
  const extendedDataModelName = getExtensionLedgerByDataModelName(ledgerDataModel)?.extendsDataModelName;
  return extendedDataModelName !== undefined && dimLedgers.includes(extendedDataModelName);
};
